<script setup lang="ts">
import { formatDateTime } from '@metapic-apps/shared'
import { computed } from 'vue'

import type { AdvertiserReport } from '@/lib/adapters/advertiser-report.adapter'
import { formatUtcDate } from '@/lib/helpers'
import { BoxComponent, FlexComponent, TagComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  report: AdvertiserReport
}>()

const emit = defineEmits<{
  open: []
}>()

const pagesLabel = `${props.report.pagesCount} ${props.report.pagesCount === 1 ? 'page' : 'pages'}`

// Only show the default period when the report has one (both timestamps set).
const defaultPeriodLabel = computed(() =>
  props.report.defaultPeriodStart && props.report.defaultPeriodEnd
    ? `${formatUtcDate(props.report.defaultPeriodStart)} – ${formatUtcDate(props.report.defaultPeriodEnd)}`
    : null,
)
</script>

<template>
  <BoxComponent :p="24" class="report-item" role="button" tabindex="0" @click="emit('open')" @keydown.enter="emit('open')">
    <FlexComponent justify="space-between" align="center" :gap="16">
      <FlexComponent direction="column" :gap="4">
        <TypographyComponent variant="title-large">{{ report.name }}</TypographyComponent>
        <TypographyComponent variant="body-small" color="var(--secondary-48)">
          Created {{ formatDateTime(report.createdAt) }}
        </TypographyComponent>
        <TypographyComponent v-if="defaultPeriodLabel" variant="body-small" color="var(--secondary-48)">
          Period: {{ defaultPeriodLabel }}
        </TypographyComponent>
      </FlexComponent>

      <FlexComponent align="center" :gap="8">
        <TagComponent type="info">{{ report.locale.toUpperCase() }}</TagComponent>
        <TagComponent type="info">{{ pagesLabel }}</TagComponent>
      </FlexComponent>
    </FlexComponent>
  </BoxComponent>
</template>

<style scoped lang="scss">
.report-item {
  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);
  cursor: pointer;

  &:hover {
    border-color: var(--secondary-48);
  }
}
</style>
